import { Link } from "@tanstack/react-router";
import { countyLabel, countyNoun } from "@/lib/county-label";
import type { StateInfo } from "@/lib/states";
import { RiskBadge } from "@/components/risk-badge";
import { bucketFor, type LayerData } from "@/lib/map/layers";
import { layerLabel } from "@/lib/map/search-params";
import { countyPagesEnabled } from "@/lib/states";
import { riskHeadline } from "@/lib/risk";
import { SeasonNow } from "@/components/season-now";

export interface SelectedCounty {
  fips: string;
  name: string;
  slug: string;
}

/**
 * What the map says about the thing you just tapped. Answer first (level + what the
 * current layer shows), then the evidence, then the next step — the same order every
 * page on the site uses.
 *
 * Rendered into a sidebar on lg+ and into a bottom sheet on phones; it owns no
 * layout of its own so both hosts can size it.
 */
export function MapDetail({
  state,
  county,
  layer,
  onClearCounty,
}: {
  state: StateInfo;
  county?: SelectedCounty | null;
  layer: LayerData;
  /** Step back out to the whole state. Only meaningful while a county is selected. */
  onClearCounty?: () => void;
}) {
  const bucket = bucketFor(layer, state.abbr);
  const detail = layer.detail?.get(state.abbr);
  const hasCountyPages = countyPagesEnabled(state);

  return (
    <div className="space-y-4">
      <div>
        <p className="label-caps text-muted-foreground">
          {county ? `${state.name} · ${countyNoun(state.abbr)} view` : "Selected state"}
        </p>
        <h2 className="font-display text-2xl font-medium leading-tight text-foreground">
          {county ? countyLabel({ ...county, stateAbbr: state.abbr }) : state.name}
        </h2>
        <div className="mt-2 flex flex-wrap gap-2">
          <RiskBadge level={state.risk} />
        </div>
        <p className="mt-3 font-display text-lg leading-snug text-foreground">
          {riskHeadline(state.risk, state.name)}
        </p>
      </div>

      {/* The awareness layer's bucket is the badge above; only other layers add something. */}
      {layer.id !== "awareness" && (
        <div className="border-y border-border py-3">
          <p className="label-caps text-muted-foreground">{layerLabel[layer.id]}</p>
          <p className="mt-1 text-sm font-medium text-foreground">{bucket.label}</p>
          <p className="mt-1 text-xs leading-snug text-muted-foreground">{bucket.meaning}</p>
          {detail && detail.length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {detail.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {county && (
        <p className="border-l-[3px] border-amber py-0.5 pl-3 text-xs leading-snug text-foreground">
          This is {state.name}&rsquo;s statewide level. Public data isn&rsquo;t detailed enough to
          rate single counties.
          {!hasCountyPages &&
            " We don't have a page for this county yet, so the link opens the state page."}
        </p>
      )}

      <div className="space-y-1.5 text-sm">
        <p className="flex items-start gap-1.5 text-muted-foreground">
          <span>
            Peak season: <span className="text-foreground">{state.peakSeason}</span>
          </span>
        </p>
        <SeasonNow peakSeason={state.peakSeason} className="text-muted-foreground" />
        <p className="text-muted-foreground">{state.summary}</p>
      </div>

      <div className="flex flex-col gap-2">
        {county && hasCountyPages ? (
          <Link
            to="/states/$state/$county"
            params={{ state: state.slug, county: county.slug }}
            className="inline-flex items-center justify-center rounded-sm bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-surface hover:bg-primary-dark"
          >
            Open {countyLabel({ ...county, stateAbbr: state.abbr })}
          </Link>
        ) : (
          <Link
            to="/states/$state"
            params={{ state: state.slug }}
            className="inline-flex items-center justify-center rounded-sm bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-surface hover:bg-primary-dark"
          >
            Open the {state.name} page
          </Link>
        )}

        {county && onClearCounty && (
          <button
            type="button"
            onClick={onClearCounty}
            className="inline-flex items-center justify-center gap-1.5 rounded-sm border border-foreground/30 px-4 py-3 text-sm font-semibold text-foreground transition-surface hover:border-foreground"
          >
            Back to all of {state.name}
          </button>
        )}

        <Link
          to="/tick-bite-tool"
          className="inline-flex items-center justify-center rounded-sm border border-foreground/30 px-4 py-3 text-sm font-semibold text-foreground transition-surface hover:border-foreground"
        >
          What to do after a bite
        </Link>
      </div>
    </div>
  );
}

export function MapDetailEmpty({ hint }: { hint: string }) {
  return (
    <div className="space-y-2">
      <h2 className="font-display text-lg font-medium text-foreground">Pick a place</h2>
      <p className="text-sm leading-relaxed text-muted-foreground">{hint}</p>
    </div>
  );
}
